import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuItem,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AddCardIcon from "@mui/icons-material/AddCard";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { SiTether } from "react-icons/si";
import Preloader from "../components/Preloader";
import {
  calculateTotalCurrencyAmount,
  getCurrencyCode,
  handleLogout,
  moneyFormat,
} from "../utils/utils";
import { useWallet } from "../api/hooks/useWallet";
import { getOrganizationSettings } from "../utils/storage";

export default function Wallets({ setSidebarOpen }) {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [error, setError] = useState("");


  // Wallet overview currency selection
  const [overviewCurrency, setOverviewCurrency] = useState("NGN");
  const [overviewTotal, setOverviewTotal] = useState(0);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  // Supported currencies for overview
  const supportedCurrencies = ["NGN", "USD", "GBP"];

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const { data: walletsData, isLoading } = useWallet();
  const {
    short_name,
    organization_subscribed_features,
    customized_app_displayable_menu_items,
  } = getOrganizationSettings();

  useEffect(() => {
    if (walletsData) {
      setWallets(walletsData.data ?? []);
      setSelectedWallet(walletsData.data[0]);
      setOverviewTotal(
        calculateTotalCurrencyAmount(walletsData.data, overviewCurrency)
      );
    }
  }, [walletsData]);

  useEffect(() => {
    if (walletsData) {
      setOverviewTotal(
        calculateTotalCurrencyAmount(walletsData.data, overviewCurrency)
      );
    }
  }, [overviewCurrency]);



  // Calculate total in selected currency
  // useEffect(() => {
  //   async function fetchTotals() {
  //     if (!wallets.length) return;
  //     setOverviewLoading(true);
  //     setOverviewError("");
  //     let total = 0;
  //     try {
  //       for (const w of wallets) {
  //         const from = getCurrencyCode(w);
  //         const amount = parseFloat(w.balance || 0);
  //         if (from === overviewCurrency) {
  //           total += amount;
  //         } else if (amount > 0) {
  //           // Convert to selected currency
  //           const res = await getExchangeRate({
  //             from: from,
  //             to: overviewCurrency,
  //             amount: amount,
  //           });
  //           if (res.status && res.data && res.data.quote_amount) {
  //             total += parseFloat(res.data.quote_amount);
  //           }
  //         }
  //       }
  //       setOverviewTotal(total);
  //     } catch (e) {
  //       setOverviewError("Failed to calculate total");
  //     }
  //     setOverviewLoading(false);
  //   }
  //   fetchTotals();
  // }, [wallets, overviewCurrency]);

  const handleSwitch = (e) => {
    const wallet = wallets.find((w) => w.id === e.target.value);
    setSelectedWallet(wallet);
  };

  // Navigation handlers for transaction pages
  const handleOpenWalletFunding = (wallet) => {
    navigate(`/wallet-funding/${wallet.wallet_number || wallet.id}`);
  };

  const handleOpenWalletConvert = (wallet) => {
    navigate(`/wallet-convert/${wallet.wallet_number || wallet.id}`);
  };

  const handleOpenCryptoTransfer = (wallet) => {
    if (!isCryptoWallet(wallet)) {
      setSnackbarMsg("Only crypto wallets can be used for crypto transfers");
      setSnackbarOpen(true);
      return;
    }
    navigate(`/crypto-transfer/${wallet.wallet_number || wallet.id}`);
  };

  const handleOpenWalletTransfer = (wallet) => {
    navigate(`/wallet-transfer/${wallet.wallet_number || wallet.id}`);
  };

  // Helper functions
  function getFlag(code) {
    if (code === "NGN") return "🇳🇬";
    if (code === "USD") return "🇺🇸";
    if (code === "USDT") return "USDT"; // Special case for USDT icon
    if (code === "GBP") return "🇬🇧";
    return "💰";
  }

  function getSymbol(code) {
    if (code === "NGN") return "₦";
    if (code === "USD" || code === "USDT") return "$";
    if (code === "GBP") return "£";
    return "";
  }

  function getTotalBalance(wallets) {
    return wallets.reduce((sum, w) => sum + (parseFloat(w.balance) || 0), 0);
  }

  function getStatusLabel(wallet) {
    // Handle case where status might be an object
    const status =
      typeof wallet.status === "object"
        ? wallet.status?.label || wallet.status?.name
        : wallet.status;

    if (status === "active") return "Active";
    if (status === "suspended") return "Suspended";
    if (status === "pending") return "Pending";
    return status || "Unknown";
  }

  function getValidWalletNumber(wallet) {
    if (!wallet) return null;
    if (wallet.wallet_number && wallet.wallet_number.length > 0) {
      return wallet.wallet_number;
    }
    return null;
  }

  function isCryptoWallet(wallet) {
    const cryptoCurrencies = [
      "BTC",
      "ETH",
      "USDT",
      "USDC",
      "BNB",
      "ADA",
      "DOT",
      "LINK",
      "LTC",
      "BCH",
      "XRP",
    ];
    const currencyCode =
      typeof wallet.currency === "object"
        ? wallet.currency?.code
        : wallet.currency;
    return cryptoCurrencies.includes(currencyCode);
  }

  if (isLoading)
    return <Preloader fullScreen={true} message="Loading wallets..." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/20 to-primary-300/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-indigo-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-white/20 px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <button
              onClick={() => setSidebarOpen && setSidebarOpen(true)}
              className="block xl:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <MenuIcon className="text-xl sm:text-2xl" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent truncate">
                My Wallets
              </h1>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base truncate">
                Manage your digital wallets and transactions
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
          >
            <LogoutIcon className="text-xl sm:text-2xl" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Overview Section */}
          <div className="fintech-card p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                  Total Balance
                </h2>
                <div className="flex items-center gap-2">
                  {overviewLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {getSymbol(overviewCurrency)}
                      {overviewTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  )}
                  <Select
                    value={overviewCurrency}
                    onChange={(e) => setOverviewCurrency(e.target.value)}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    {supportedCurrencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        <div className="flex items-center gap-2">
                          {/* {currency === "USDT" ? (
                            <SiTether className="text-lg" />
                          ) : (
                            getFlag(currency)
                          )} */}
                          {currency}
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                {overviewError && (
                  <p className="text-red-600 text-sm mt-1">{overviewError}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
              {organization_subscribed_features[
                "cross-border-payment-mgt"
              ] &&
                customized_app_displayable_menu_items[
                "display-fiat-crypto-conversion-options"
                ] && (<button
                  onClick={() =>
                    selectedWallet && handleOpenWalletConvert(selectedWallet)
                  }
                  disabled={!selectedWallet}
                  className="btn-primary flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl text-sm sm:text-base"
                >
                  <AddCardIcon className="text-lg sm:text-xl" />
                  <span className="hidden sm:inline">Add Funds</span>
                  <span className="sm:hidden">Fund</span>
                </button>)}
              {organization_subscribed_features[
                "cross-border-payment-mgt"
              ] &&
                customized_app_displayable_menu_items[
                "display-fiat-crypto-conversion-options"
                ] && (
                  <button
                    onClick={() =>
                      selectedWallet && handleOpenWalletConvert(selectedWallet)
                    }
                    disabled={!selectedWallet}
                    className="btn-secondary flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl text-sm sm:text-base"
                  >
                    <CurrencyExchangeIcon className="text-lg sm:text-xl" />
                    <span className="hidden sm:inline">Convert Funds</span>
                    <span className="sm:hidden">Convert</span>
                  </button>
                )}

              {organization_subscribed_features[
                "payments-manager"
              ] &&
                customized_app_displayable_menu_items[
                "display-bank-transfer-menu"
                ] && (
                  <button
                    onClick={() =>
                      selectedWallet && handleOpenWalletTransfer(selectedWallet)
                    }
                    disabled={!selectedWallet}
                    className="btn-secondary flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl text-sm sm:text-base"
                  >
                    <PaymentsIcon className="text-lg sm:text-xl" />
                    <span className="hidden sm:inline">Send Money</span>
                    <span className="sm:hidden">Send</span>
                  </button>
                )}
            </div>

            {/* Wallet Selection */}
            {wallets.length > 0 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                <label className="text-sm font-medium text-gray-700">
                  Selected Wallet:
                </label>
                <Select
                  value={selectedWallet?.id || ""}
                  onChange={handleSwitch}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  {wallets.map((wallet) => (
                    <MenuItem key={wallet.id} value={wallet.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-6 h-6 rounded-lg flex items-center justify-center text-sm ${getCurrencyCode(wallet) === "NGN"
                            ? "bg-green-100 text-green-700"
                            : getCurrencyCode(wallet) === "USD"
                              ? "bg-blue-100 text-blue-700"
                              : getCurrencyCode(wallet) === "USDT"
                                ? "bg-green-100 text-green-700"
                                : getCurrencyCode(wallet) === "GBP"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                        >
                          {getCurrencyCode(wallet) === "USDT" ? (
                            <SiTether className="text-sm" />
                          ) : (
                            getFlag(getCurrencyCode(wallet))
                          )}
                        </div>
                        <span>
                          {typeof wallet.ownership_label === "string"
                            ? wallet.ownership_label
                            : `${getCurrencyCode(wallet)} Wallet`}
                        </span>
                        <span className="text-gray-500 text-xs">
                          (
                          {typeof wallet.wallet_number === "string"
                            ? wallet.wallet_number
                            : "N/A"}
                          )
                        </span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}
          </div>

          {/* Wallets Section */}
          <div className="fintech-card p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Your Wallets
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Manage your digital wallets and perform transactions
                </p>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden xl:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">
                        Wallet
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">
                        Balance
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallets.map((wallet) => (
                      <tr
                        key={wallet.id}
                        className="border-b border-gray-100 hover:bg-gray-50/50"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg shadow-sm ${getCurrencyCode(wallet) === "NGN"
                                ? "bg-green-100 text-green-700"
                                : getCurrencyCode(wallet) === "USD"
                                  ? "bg-blue-100 text-blue-700"
                                  : getCurrencyCode(wallet) === "USDT"
                                    ? "bg-green-100 text-green-700"
                                    : getCurrencyCode(wallet) === "GBP"
                                      ? "bg-purple-100 text-purple-700"
                                      : "bg-gray-100 text-gray-700"
                                }`}
                            >
                              {getCurrencyCode(wallet) === "USDT" ? (
                                <SiTether className="text-lg sm:text-xl" />
                              ) : (
                                getFlag(getCurrencyCode(wallet))
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm sm:text-base font-semibold text-gray-900">
                                {typeof wallet.ownership_label === "string"
                                  ? wallet.ownership_label
                                  : `${getCurrencyCode(wallet)} Wallet`}
                              </div>
                              <div className="row">
                                <div className="text-xs sm:text-sm text-gray-500">
                                  {typeof wallet.wallet_number === "string"
                                    ? wallet.wallet_number
                                    : "N/A"}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-500">
                                  {"("}{wallet.bank.name != undefined ? wallet.bank.name : short_name}{")"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-sm sm:text-base font-bold text-gray-900">
                            {getSymbol(getCurrencyCode(wallet))}
                            {parseFloat(
                              !isNaN(wallet.balance) ? wallet.balance : 0
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusLabel(wallet) === "Active"
                              ? "bg-green-100 text-green-800"
                              : getStatusLabel(wallet) === "Suspended"
                                ? "bg-red-100 text-red-800"
                                : getStatusLabel(wallet) === "Pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {getStatusLabel(wallet)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-2">
                            {organization_subscribed_features[
                              "cross-border-payment-mgt"
                            ] &&
                              customized_app_displayable_menu_items[
                              "display-fiat-crypto-conversion-options"
                              ] && (
                                <button
                                  onClick={() => handleOpenWalletFunding(wallet)}
                                  disabled={
                                    !wallet.wallet_number ||
                                    typeof wallet.wallet_number !== "string"
                                  }
                                  className="btn-primary text-xs py-2 px-3 rounded-lg font-medium"
                                  title="Add Funds"
                                >
                                  <div className="flex items-center gap-1">
                                    <AddCardIcon className="w-3 h-3" />
                                    <span className="hidden sm:inline">Fund</span>
                                  </div>
                                </button>)}
                            {isCryptoWallet(wallet) ? (
                              <button
                                onClick={() => handleOpenCryptoTransfer(wallet)}
                                disabled={
                                  !wallet.wallet_number ||
                                  typeof wallet.wallet_number !== "string"
                                }
                                className="btn-secondary text-xs py-2 px-3 rounded-lg font-medium"
                                title="Send Crypto"
                              >
                                <div className="flex items-center gap-1">
                                  <CurrencyExchangeIcon className="w-3 h-3" />
                                  <span className="hidden sm:inline">
                                    Send Crypto
                                  </span>
                                </div>
                              </button>
                            ) : (
                              <>
                                {organization_subscribed_features[
                                  "cross-border-payment-mgt"
                                ] &&
                                  customized_app_displayable_menu_items[
                                  "display-fiat-crypto-conversion-options"
                                  ] && (
                                    <button
                                      onClick={() =>
                                        handleOpenWalletConvert(wallet)
                                      }
                                      disabled={
                                        !wallet.wallet_number ||
                                        typeof wallet.wallet_number !== "string"
                                      }
                                      className="btn-secondary text-xs py-2 px-3 rounded-lg font-medium"
                                      title="Convert Funds"
                                    >
                                      <div className="flex items-center gap-1">
                                        <CurrencyExchangeIcon className="w-3 h-3" />
                                        <span className="hidden sm:inline">
                                          Convert
                                        </span>
                                      </div>
                                    </button>
                                  )}
                                {organization_subscribed_features[
                                  "payments-manager"
                                ] &&
                                  customized_app_displayable_menu_items[
                                  "display-bank-transfer-menu"
                                  ] && (
                                    <button
                                      onClick={() =>
                                        handleOpenWalletTransfer(wallet)
                                      }
                                      disabled={
                                        !wallet.wallet_number ||
                                        typeof wallet.wallet_number !== "string"
                                      }
                                      className="btn-secondary text-xs py-2 px-3 rounded-lg font-medium"
                                      title="Send Money"
                                    >
                                      <div className="flex items-center gap-1">
                                        <PaymentsIcon className="w-3 h-3" />
                                        <span className="hidden sm:inline">
                                          Send
                                        </span>
                                      </div>
                                    </button>
                                  )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="xl:hidden space-y-3 sm:space-y-4">
              {wallets.map((wallet) => {
                return (
                  <div
                    key={wallet.id}
                    className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-base sm:text-lg shadow-sm ${getCurrencyCode(wallet) === "NGN"
                          ? "bg-green-100 text-green-700"
                          : getCurrencyCode(wallet) === "USD"
                            ? "bg-blue-100 text-blue-700"
                            : getCurrencyCode(wallet) === "USDT"
                              ? "bg-green-100 text-green-700"
                              : getCurrencyCode(wallet) === "GBP"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {getCurrencyCode(wallet) === "USDT" ? (
                          <SiTether className="text-lg sm:text-xl" />
                        ) : (
                          getFlag(getCurrencyCode(wallet))
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm sm:text-base font-semibold text-gray-900">
                          {typeof wallet.ownership_label === "string"
                            ? wallet.ownership_label
                            : `${getCurrencyCode(wallet)} Wallet`}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 truncate">
                          {typeof wallet.wallet_number === "string"
                            ? wallet.wallet_number
                            : "N/A"}
                        </div>

                        <div className="text-xs sm:text-sm text-gray-500">
                          {"("}{wallet.bank.name != undefined ? wallet.bank.name : short_name}{")"}
                        </div>
                        <div className="text-sm sm:text-base font-bold text-gray-900 mt-1">
                          {getSymbol(getCurrencyCode(wallet))}
                          {moneyFormat(wallet.balance)}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusLabel(wallet) === "Active"
                          ? "bg-green-100 text-green-800"
                          : getStatusLabel(wallet) === "Suspended"
                            ? "bg-red-100 text-red-800"
                            : getStatusLabel(wallet) === "Pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {getStatusLabel(wallet)}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenWalletFunding(wallet)}
                        disabled={
                          !wallet.wallet_number ||
                          typeof wallet.wallet_number !== "string"
                        }
                        className="flex-1 btn-primary text-xs py-2 sm:py-2.5 rounded-lg font-medium"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <AddCardIcon className="w-3 h-3" />
                          <span className="hidden sm:inline">Fund</span>
                          <span className="sm:hidden">Fund</span>
                        </div>
                      </button>
                      {isCryptoWallet(wallet) ? (
                        <button
                          onClick={() => handleOpenCryptoTransfer(wallet)}
                          disabled={
                            !wallet.wallet_number ||
                            typeof wallet.wallet_number !== "string"
                          }
                          className="flex-1 btn-secondary text-xs py-2 sm:py-2.5 rounded-lg font-medium"
                        >
                          <div className="flex items-center justify-center gap-1">
                            <CurrencyExchangeIcon className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              Send Crypto
                            </span>
                            <span className="sm:hidden">Send</span>
                          </div>
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleOpenWalletConvert(wallet)}
                            disabled={
                              !wallet.wallet_number ||
                              typeof wallet.wallet_number !== "string"
                            }
                            className="flex-1 btn-secondary text-xs py-2 sm:py-2.5 rounded-lg font-medium"
                          >
                            <div className="flex items-center justify-center gap-1">
                              <CurrencyExchangeIcon className="w-3 h-3" />
                              <span className="hidden sm:inline">Convert</span>
                              <span className="sm:hidden">Conv</span>
                            </div>
                          </button>
                          <button
                            onClick={() => handleOpenWalletTransfer(wallet)}
                            disabled={
                              !wallet.wallet_number ||
                              typeof wallet.wallet_number !== "string"
                            }
                            className="flex-1 btn-secondary text-xs py-2 sm:py-2.5 rounded-lg font-medium"
                          >
                            <div className="flex items-center justify-center gap-1">
                              <PaymentsIcon className="w-3 h-3" />
                              <span className="hidden sm:inline">
                                Send Money
                              </span>
                              <span className="sm:hidden">Send</span>
                            </div>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {wallets.length === 0 && (
              <div className="text-center py-8 sm:py-12">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <AccountBalanceWalletIcon className="text-gray-400 text-2xl sm:text-4xl" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  No Wallets Found
                </h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base px-4">
                  You don't have any wallets yet. Contact support to create your
                  first wallet.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="info"
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}
